import { NgClass } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from 'primeng/api';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Menu, MenuModule } from 'primeng/menu';
import { Subject, take } from 'rxjs';
import { SignDocumentComponent } from '../../../pages/private/mi-cigna/components/sign-document/sign-document.component';
import {
  Documentos,
  Notification,
  RecordsNotifications,
} from '../../../pages/private/mi-cigna/models/mi-cigna';
import { MiCignaService } from '../../../pages/private/mi-cigna/services/mi-cigna.service';
import { CignaIconComponent } from '../../../shared/components/cigna-icon/cigna-icon.component';
import { StorageKeys } from '../../../shared/enums/storage-keys.enum';
import { InsuredData } from '../../../shared/models/user-data';
import { AuthService } from '../../../shared/services/auth.service';
import { MessageService } from '../../../shared/services/message.service';
import { ModalService } from '../../../shared/services/modal.service';
import { StorageService } from '../../../shared/services/storage.service';
import { UtilsService } from '../../../shared/services/utils.service';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  standalone: true,
  imports: [NgClass, CignaIconComponent, MenuModule, SharedModule, TranslateModule],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  showModal = false;
  @ViewChild('menu6') menu!: Menu;
  menuOpcion: { [key: string]: boolean } = {};

  @Output() notificationClicked = new EventEmitter<boolean>();

  private _authService = inject(AuthService);
  private _storageService = inject(StorageService);
  private _miCignaService = inject(MiCignaService);
  private _messageService = inject(MessageService);
  private _utilsService = inject(UtilsService);
  private _router: Router = inject(Router);
  private _modalService = inject(ModalService);

  logged: boolean = false;
  notifications!: RecordsNotifications;
  unreadNotifications!: RecordsNotifications;

  showMenu: boolean = false;
  showCover: boolean = false;
  logData!: InsuredData;
  notificationsPage: number = 0;
  pageSize: number = 3;
  selectedNotification!: Notification;
  closed: boolean = true;
  showMoreButton: boolean = false;
  private readonly destroySubject$ = new Subject<void>();
  unreadCount: number = 0;
  language: string = 'es';
  doc: Documentos | null = null;

  ngOnInit(): void {
    this._authService.logged$.subscribe((val: boolean) => {
      if (this.logged !== val) {
        this.logged = val;
        const isAdmin = this._storageService.sessionStorageGetItem(
          StorageKeys.LOGIN_DATA
        )?.isAdmin;
        if (!isAdmin || (isAdmin && this._utilsService.isSimulating())) {
          this.getNotifications();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
  }

  reload(event: any): void {
    if (this.closed) {
      const isAdmin = this._storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA).isAdmin;
      if (!isAdmin || (isAdmin && this._utilsService.isSimulating())) {
        this.notificationsPage = 0;
        this.getNotifications();
        this.closed = false;
        this.menu.show(event);
      }
    } else {
      this.closed = true;
      this.menu.hide();
    }
  }

  getNotifications(): void {
    this.logData = this._storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA);
    this.language = this._storageService.localStorageGetItem(StorageKeys.LANGUAGE);

    if (this.logData.insuranceNumber) {
      this._miCignaService
        .getNotifications(this.logData.insuranceNumber, this.notificationsPage, this.pageSize)
        .pipe(take(1))
        .subscribe(data => {
          this.notifications = (data as any).notifications;
          this.showMoreButton = !this.notifications.last;
        });

      this._miCignaService
        .getUnreadNotifications(
          this.logData.insuranceNumber,
          this.notificationsPage,
          this.pageSize
        )
        .pipe(take(1))
        .subscribe(data => {
          this.unreadNotifications = (data as any).notifications;
          this.unreadCount = this.unreadNotifications.totalElements;
        });
    }
  }

  openMenu(opcion: string): void {
    this.showCover = true;
    this.menuOpcion[opcion] = true;
    this.closed = false;
  }

  closeMenu(opcion: string): void {
    this.notificationsPage = 0;
    this.showCover = false;
    this.menuOpcion[opcion] = false;
    this.closed = true;
  }

  viewMore(): void {
    this.notificationsPage++;
    this.getNotifications();
  }

  downloadFile(documentId: number, name: string, notificationId: number): void {
    this.logData = this._storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA);
    this._miCignaService
      .postNotificationReading(this.logData.insuranceNumber, notificationId)
      .pipe(take(1))
      .subscribe(() => {
        /* console.log(data); */
      });

    this._miCignaService
      .downloadFile(documentId, this.logData.insuranceNumber)
      .pipe(take(1))
      .subscribe((data: any) => {
        if (data.type.indexOf('json') > 0 || data.type.indexOf('xml') > 0) {
          this._messageService.showMessage('error', 'back_errors.download_document');
        } else {
          this._utilsService.saveFile(data, name);
        }
        this.getNotifications();
      });
  }

  labelAllAsRead(): void {
    this.notifications.content.forEach(n => (n.read = true));
    this._miCignaService
      .postAllNotificationReading()
      .pipe(take(1))
      .subscribe(() => {
        this.getNotifications();
      });
  }

  redirect(notificationId: number, destiny: string): void {
    this.logData = this._storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA);
    this.closeMenu('notificaciones');
    this.menu.hide();
    this._miCignaService
      .postNotificationReading(this.logData.insuranceNumber, notificationId)
      .pipe(take(1))
      .subscribe(() => {});
    this.getNotifications();
    this._router.navigate([destiny]);
  }

  deleteNotification(notificationId: number): void {
    this.logData = this._storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA);
    this._miCignaService
      .postDeleteNotification(this.logData.insuranceNumber, notificationId)
      .pipe(take(1))
      .subscribe(() => {
        this.getNotifications();
      });
  }

  openSign(documentId: number, signature: string, notificationId: number): void {
    this.logData = this._storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA);
    this._miCignaService
      .postNotificationReading(this.logData.insuranceNumber, notificationId)
      .pipe(take(1))
      .subscribe(() => {
        /* console.log(data); */
      });

    this._miCignaService
      .getDocument(this.logData.insuranceNumber, documentId)
      .pipe(take(1))
      .subscribe(data => {
        this.doc = data as Documentos;
        const ref: DynamicDialogRef = this._modalService.showModal(SignDocumentComponent, {
          document: this.doc,
          signature: signature,
        });

        ref.onClose.subscribe((newStatus: string) => {
          if (newStatus && this.doc != null) {
            this.doc.signatureStatus = newStatus;
            this.doc.acceptanceNeeded = false;
            this.doc.signNeeded = false;
            this._miCignaService.updateDocumentStatus(this.doc.id, newStatus);
          }
        });
      });
  }
}
